import { type FormState, getFormAsState } from '@brightdrop/bd-form';
import { SearchType } from '@brightdrop/bd-ui';
import { isValidLocation } from '@brightdrop/map-ui/utils';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import mapKeys from 'lodash/mapKeys';
import omitBy from 'lodash/omitBy';
import set from 'lodash/set';
import type { RouteParams } from '~/common/configs/route.config';
import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import {
  type DiagnosticValueFormat,
  percentLabel,
} from '~/common/localization/diagnosticFormat';
import {
  type ApiAsset,
  ASSET_SEARCH_FIELD_NAMES,
  type Asset,
  type AssetEvent,
  type AssetFlags,
  type AssetPubSubEventPayload,
  type AssetRequestFieldNames,
  AssetSearchFieldType,
  type AssetServiceStatus,
  type AssetState,
  type AssetTelemetry,
  type AssetTrip,
  AssetType,
  OnstarConnectionStatus,
  OnstarSupportStatus,
  OrganizationOwnershipStatus,
  type VehRejectApproveForm,
} from '~/common/models/asset.model';
import {
  ASSET_REPORT_FIELD_NAMES,
  AssetComplianceFieldType,
  AssetReportFieldType,
  BatteryChargingStatus,
  type BatterySocStatus,
  BrakePadStatus,
  type EstimatedRangeStatus,
  type FuelStatus,
  LastCommunicatedStatus,
  OilLifeStatus,
  PowerType,
  ReportDiagnosticState,
  SafetyCompliance,
} from '~/common/models/asset-report.model';
import {
  ConnectivityStatusType,
  type SearchCriteria,
} from '~/common/models/common.model';
import {
  type BDQuery,
  BDQueryCriteriaType,
  type BDSort,
  type BDSortDirection,
} from '~/common/models/query.model';
import { MAX_VALID_SOC_VALUE } from '~/common/utils/battery.utils';
import { buildQueryTree } from '~/common/utils/query.utils';

import type {
  AssetComplianceFilterCriteria,
  AssetComplianceSession,
  AssetListFilterCriteria,
  AssetListSession,
} from '../assetsSlice';
import { getLastCommunicatedDateRange } from '../list/utils/vehicle.utils';
import {
  DEFAULT_SORT_ORDER,
  getApiSortDirection,
} from '../list/utils/vehicleListSort.utils';
import { mapAssetApiResponseToAsset } from '../mappers/asset.mappers';
import {
  ESTIMATED_RANGE_MODAL_STATUS,
  FUEL_CHARGE_FILTER_MODAL_STATUS,
  MIN_VALID_ESTIMATED_RANGE_VALUE,
  MIN_VALID_FUEL_VALUE,
} from '../report/AssetReportView.constant';
import {
  AVAILABLE_LAST_COMMUNICATED_FILTER_OPTIONS,
  AVAILABLE_TIRE_FILTER_OPTIONS,
  BATTERY_READINESS_CHARGING_FILTER_OPTIONS,
} from '../report/configs/AssetListReportViewFilter.config';

// Constants for license plate formatting
export const DEFAULT_LICENSE_PLATE_VALUE = '000000';

export const getAssetsByClass = (
  assets: Asset[],
  currentTab: AssetType | undefined
): Asset[] => {
  switch (currentTab) {
    case AssetType.EP:
      return assets.filter(
        (asset) =>
          asset.type.class &&
          (asset.type.class === AssetType.EP ||
            asset.type.class === AssetType.PERI)
      );
    case AssetType.VEHICLE:
      return assets.filter(
        (asset) => asset.type.class && asset.type.class === AssetType.VEHICLE
      );
    default:
      return assets;
  }
};

export const getMappedAssetsByClass = <
  T extends Asset &
    Partial<AssetState> &
    Partial<AssetTelemetry> &
    Partial<AssetFlags>,
>(
  mappedAssets: T[]
): {
  [k in AssetType | 'all']: (T & { originalIndex: number })[];
} => {
  const assetsByClass = {} as {
    [k in AssetType | 'all']: (T & { originalIndex: number })[];
  };
  assetsByClass['all'] = [];
  if (mappedAssets.length) {
    mappedAssets.forEach((asset, index) => {
      if (asset.type.class && asset.type.class === AssetType.VEHICLE) {
        if (!assetsByClass[asset.type.class]) {
          assetsByClass[asset.type.class] = [];
        }
        assetsByClass[asset.type.class] = [
          ...assetsByClass[asset.type.class],
          { ...asset, originalIndex: index },
        ];
      }
      if (
        asset.type.class &&
        (asset.type.class === AssetType.EP ||
          asset.type.class === AssetType.PERI)
      ) {
        if (!assetsByClass[AssetType.EP]) {
          assetsByClass[AssetType.EP] = [];
        }
        assetsByClass[AssetType.EP] = [
          ...assetsByClass[AssetType.EP],
          { ...asset, originalIndex: index },
        ];
      }
      assetsByClass['all'] = [
        ...assetsByClass['all'],
        { ...asset, originalIndex: index },
      ];
    });
  }
  return assetsByClass;
};

export const getRejectApproveVehForm = (): FormState<VehRejectApproveForm> =>
  getFormAsState({
    note: null,
  } as VehRejectApproveForm);

export const filterTripLocation = (trip: AssetTrip): boolean => {
  const tripIsDefined = trip.id && trip.start?.value && trip.end?.value;
  const validTripLocations =
    isValidLocation(trip.start?.value) && isValidLocation(trip.end?.value);
  const hasDifferentEndpoints =
    trip.start?.value.latitude !== trip.end?.value.latitude ||
    trip.start?.value.longitude !== trip.end?.value.longitude;
  const vehicleDidMove =
    !trip?.startOdometer ||
    !trip.endOdometer ||
    trip?.startOdometer !== trip?.endOdometer;
  return (
    !isEmpty(tripIsDefined) &&
    validTripLocations &&
    (hasDifferentEndpoints || vehicleDidMove)
  );
};
export const getTripsFromAssetEvents = (events?: AssetEvent[]): AssetTrip[] =>
  events
    ?.reduce((accumulator: AssetTrip[], current) => {
      const lastTrip = accumulator[accumulator.length - 1];
      return (!lastTrip || (lastTrip.start && lastTrip.end)) &&
        current.action?.type === 'Power' &&
        current.action?.value === 'OFF'
        ? [
            ...accumulator,
            {
              id: current.state?.tripId || current.id,
              end: {
                deviceTimestamp: current.timestamp,
                value: {
                  ...current.state.location,
                  address: current.state.address,
                },
              },
              tripId: current.state.tripId,
              startEventId: current.state.startEventId,
              endEventId: current.state.endEventId,
              endOdometer: current.state?.tripEndOdometer,
            } as AssetTrip,
          ]
        : lastTrip?.end &&
            !lastTrip?.start &&
            current.action?.type === 'Power' &&
            current.action?.value === 'ON'
          ? [
              ...accumulator.slice(0, accumulator.length - 1),
              {
                ...lastTrip,
                id: current.state?.tripId || current.id,
                start: {
                  deviceTimestamp: current.timestamp,
                  value: {
                    ...current.state.location,
                    address: current.state.address,
                  },
                },
                tripId: current.state.tripId,
                startEventId: current.state.startEventId,
                endEventId: current.state.endEventId,
                startOdometer: current.state?.tripStartOdometer,
              } as AssetTrip,
            ]
          : accumulator;
    }, [])
    .filter((trip) => filterTripLocation(trip))
    .map((trip, index, array) => {
      const startId = 2 * array.length - 2 * index - 1;
      return {
        ...trip,
        start: {
          ...trip.start,
          value: { ...trip.start.value, id: startId },
        },
        end: {
          ...trip.end,
          value: {
            ...trip.end.value,
            id: startId + 1,
          },
        },
      };
    }) || [];

// a) Default selected tab is "VEHICLES" for any asset view showing a list with tabs, or
// b) "EPs" tab should only be selected by default if asset list contains only EP assets; in any other case, default to "VEHICLES"
export const getDefaultAssetListTab = (assets: Asset[]): AssetType => {
  const hasVehicles = assets.some(
    (asset) => asset.type?.class === AssetType.VEHICLE
  );
  const hasEP = assets.some((asset) => asset.type?.class === AssetType.EP);

  if (hasEP && hasVehicles) {
    return AssetType.VEHICLE;
  }
  if (hasEP) {
    return AssetType.EP;
  }
  return AssetType.VEHICLE;
};

export const isPubSubEventMessage = <T extends AssetPubSubEventPayload>(
  arg: unknown
): arg is T => {
  return !!arg && !!(arg as T).id && !!(arg as T).statuses;
};

export const mergeAssetStates = (
  existing: Asset,
  incoming: AssetPubSubEventPayload
): Asset => {
  // event payload `location` status uses different format compared to asset details, must be handled separately
  const { location: incomingLocation, ...statuses } = incoming.statuses || {};
  const incomingCoordinates = incomingLocation?.value?.coordinates || [];
  const incomingTimestamp = incomingLocation?.deviceTimestamp;

  const mergedApiAsset: Partial<ApiAsset> = {
    ...existing.raw,
    diagnostics: incoming.diagnostics,
    status: {
      ...existing.raw?.status,
      ...statuses,
      location:
        incomingTimestamp &&
        incomingCoordinates?.length === 2 &&
        isValidLocation({
          latitude: incomingCoordinates[1],
          longitude: incomingCoordinates[0],
        })
          ? {
              value: {
                lat: incomingCoordinates[1],
                lon: incomingCoordinates[0],
              },
              deviceTimestamp: incomingTimestamp,
            }
          : existing.raw?.status?.location,
    },
  };

  try {
    // Default to false for not-equipped flag since this utility function doesn't have access to Redux state
    const mappedAsset = mapAssetApiResponseToAsset(mergedApiAsset, false);
    return mappedAsset;
  } catch (_e) {
    // do nothing with incoming event; return existing asset
  }
  return existing;
};

export const getAssetTypeDetail = (asset: Asset): string => {
  return asset.type?.model
    ? `${asset.type.model} ${asset.type.version}`
    : DEFAULT_EMPTY_VALUE;
};

export const createAssetQueryTree = (
  queries: BDQuery[]
): BDQuery | undefined => {
  if (queries.length === 0) {
    return undefined;
  }
  return buildQueryTree(queries, 0, queries.length - 1, true);
};

export const createEqualQuery = (
  field: string,
  values: Array<string | boolean>
): BDQuery => createQuery(BDQueryCriteriaType.IS_EQUAL, field, values);

export const createIsNullQuery = (field: string): BDQuery =>
  ({
    qualifier: BDQueryCriteriaType.IS_NULL,
    field,
  }) as BDQuery;

export const createContainingQuery = (
  field: string,
  values: Array<string>
): BDQuery => createQuery(BDQueryCriteriaType.CONTAINING, field, values);

export const createInQuery = (
  field: string,
  values: Array<string | boolean>
): BDQuery => createQuery(BDQueryCriteriaType.IN, field, values);

export const createQuery = (
  qualifier: BDQueryCriteriaType,
  field: string,
  values: Array<string | boolean | string[]>
): BDQuery => {
  return {
    qualifier,
    field,
    values,
  } as BDQuery;
};

export const createOrQuery = (
  leftQuery: BDQuery,
  rightQuery: BDQuery
): BDQuery => {
  return {
    qualifier: BDQueryCriteriaType.OR,
    leftQuery,
    rightQuery,
  } as BDQuery;
};

export const createAssetFilterQuery = (
  type: AssetSearchFieldType,
  input: Array<string | boolean>
): BDQuery | undefined => {
  let criteria: BDQuery | undefined;
  switch (type) {
    case AssetSearchFieldType.FLEET_ID:
    case AssetSearchFieldType.ORG_ID: {
      criteria = {
        qualifier: BDQueryCriteriaType.IS_EQUAL,
        field: type,
        values: input,
      } as BDQuery;
      break;
    }
  }
  return criteria;
};

export const createSearchQuery = (
  searchCriteria: SearchCriteria | undefined,
  fieldNames: AssetRequestFieldNames
): BDQuery | undefined => {
  if (!searchCriteria?.input || !searchCriteria?.searchType) {
    return undefined;
  }
  const values = [searchCriteria.input];
  switch (searchCriteria.searchType) {
    case SearchType.ASSET:
      return createContainingQuery(fieldNames.name, values);
    case SearchType.COMBINED_VEHICLE_SEARCH:
      // Search both id and name fields with OR when both are available
      if (fieldNames.id && fieldNames.name) {
        return createOrQuery(
          createContainingQuery(fieldNames.id, values),
          createContainingQuery(fieldNames.name, values)
        );
      }
      // Fallback to name if available, otherwise id
      return createContainingQuery(
        fieldNames.name || fieldNames.id || AssetSearchFieldType.ASSET_NAME,
        values
      );
    case SearchType.ID:
    case SearchType.VIN:
    case SearchType.SERIAL_NUMBER:
      return createContainingQuery(fieldNames.id, values);
    case SearchType.LICENSE_PLATE:
      return createContainingQuery(
        fieldNames.licensePlate || AssetSearchFieldType.LICENSE_PLATE,
        values
      );
    default:
      return undefined;
  }
};
const createDiagnosticQuery = (
  diagnostics: ReportDiagnosticState[],
  fieldName: string
) => {
  const isCritical = diagnostics.find(
    (diagnostic) => diagnostic === ReportDiagnosticState.CRITICAL
  );
  const isGood = diagnostics.find(
    (diagnostic) => diagnostic === ReportDiagnosticState.GOOD
  );
  if (isCritical && isGood) {
    return undefined;
  }
  if (isCritical) {
    return createEqualQuery(fieldName, [true]);
  }
  if (isGood) {
    return createIsNullQuery(fieldName);
  }
  return undefined;
};
const createSafetyCompliantQuery = (
  compliance: SafetyCompliance[],
  safetyCompliantFieldName: string,
  chargingCompliantFieldName: string,
  isChargeStatusFlagEnabled: boolean
) => {
  const compliant = compliance.find(
    (compliance) => compliance === SafetyCompliance.COMPLIANT
  );
  if (compliant) {
    if (isChargeStatusFlagEnabled) {
      return {
        qualifier: BDQueryCriteriaType.AND,
        leftQuery: {
          qualifier: BDQueryCriteriaType.OR,
          leftQuery: {
            qualifier: BDQueryCriteriaType.IS_EQUAL,
            field: safetyCompliantFieldName,
            values: [true],
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.IS_NULL,
            field: safetyCompliantFieldName,
          },
        },
        rightQuery: {
          qualifier: BDQueryCriteriaType.OR,
          leftQuery: {
            qualifier: BDQueryCriteriaType.IS_EQUAL,
            field: chargingCompliantFieldName,
            values: [true],
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.IS_NULL,
            field: chargingCompliantFieldName,
          },
        },
      } as BDQuery;
    }
    return {
      qualifier: BDQueryCriteriaType.OR,
      leftQuery: {
        qualifier: BDQueryCriteriaType.IS_EQUAL,
        field: safetyCompliantFieldName,
        values: [true],
      },
      rightQuery: {
        qualifier: BDQueryCriteriaType.IS_NULL,
        field: safetyCompliantFieldName,
      },
    };
  }
  if (isChargeStatusFlagEnabled) {
    return {
      qualifier: BDQueryCriteriaType.OR,
      leftQuery: {
        qualifier: BDQueryCriteriaType.IS_EQUAL,
        field: safetyCompliantFieldName,
        values: [false],
      },
      rightQuery: {
        qualifier: BDQueryCriteriaType.IS_EQUAL,
        field: chargingCompliantFieldName,
        values: [false],
      },
    } as BDQuery;
  }
  return createQuery(BDQueryCriteriaType.IS_EQUAL, safetyCompliantFieldName, [
    false,
  ]);
};

const createChargeStatusQuery = (
  batteryChargingStatus: BatteryChargingStatus[] | undefined,
  fieldName: string
) => {
  if (batteryChargingStatus?.length) {
    if (
      batteryChargingStatus.length ===
      BATTERY_READINESS_CHARGING_FILTER_OPTIONS.length
    ) {
      return undefined;
    }
    return createInQuery(fieldName, batteryChargingStatus);
  }
};

const createPowerTypeQuery = (powerTypes: PowerType[], fieldName: string) => {
  if (powerTypes.length === Object.keys(PowerType).length) {
    return undefined;
  }
  const mapPowerType: { [key: string]: string[] } = {
    DC: ['level_3'],
    AC: ['level_1', 'level_2'],
  };
  const convertedLevels = powerTypes.flatMap(
    (type: PowerType) => mapPowerType[type] || []
  );
  return {
    qualifier: BDQueryCriteriaType.AND,
    leftQuery: {
      qualifier: BDQueryCriteriaType.IN,
      field: fieldName,
      values: convertedLevels,
    },
    rightQuery: {
      qualifier: BDQueryCriteriaType.NOT_IN,
      field: AssetReportFieldType.CHARGING_STATUS,
      values: [BatteryChargingStatus.UNPLUGGED, BatteryChargingStatus.PLUGGED],
    }, //TODO:Remove the condition when API is ready to handle the charging status for UNPLUGGED and PLUGGED case
  } as BDQuery;
};

export const OFFLINE_MINUTES_THRESHOLD = 10;

const createLastCommunicatedQuery = (
  lastCommunicated: LastCommunicatedStatus[],
  field: string
) => {
  if (
    lastCommunicated.length ===
    AVAILABLE_LAST_COMMUNICATED_FILTER_OPTIONS.length
  ) {
    return undefined;
  }
  const queries = lastCommunicated
    .map((status) => {
      const range = getLastCommunicatedDateRange(status);
      if (status === LastCommunicatedStatus.WITHIN_M1W) {
        return (
          range.start &&
          ({
            qualifier: BDQueryCriteriaType.LESS_THAN,
            field,
            values: [range.start.toISOString()],
          } as BDQuery)
        );
      }
      return range.end && range.start
        ? ({
            qualifier: BDQueryCriteriaType.AND,
            leftQuery: {
              qualifier: BDQueryCriteriaType.OR,
              leftQuery: {
                qualifier: BDQueryCriteriaType.GREATER_THAN,
                field,
                values: [range.start.toISOString()],
              },
              rightQuery: {
                qualifier: BDQueryCriteriaType.IS_EQUAL,
                field,
                values: [range.start.toISOString()],
              },
            },
            rightQuery: {
              qualifier: BDQueryCriteriaType.LESS_THAN,
              field,
              values: [range.end.toISOString()],
            },
          } as BDQuery)
        : range.end
          ? ({
              qualifier: BDQueryCriteriaType.LESS_THAN,
              field,
              values: [range.end.toISOString()],
            } as BDQuery)
          : range.start
            ? ({
                qualifier: BDQueryCriteriaType.OR,
                leftQuery: {
                  qualifier: BDQueryCriteriaType.GREATER_THAN,
                  field,
                  values: [range.start.toISOString()],
                },
                rightQuery: {
                  qualifier: BDQueryCriteriaType.IS_EQUAL,
                  field,
                  values: [range.start.toISOString()],
                },
              } as BDQuery)
            : undefined;
    })
    .filter((query) => !!query) as BDQuery[];
  return buildQueryTree(queries, 0, queries.length - 1, false);
};

const createTiresQuery = (connectivityStatus: ReportDiagnosticState[]) => {
  if (connectivityStatus.length === AVAILABLE_TIRE_FILTER_OPTIONS.length) {
    return undefined;
  }
  const queries = connectivityStatus.map((status) => {
    switch (status) {
      case ReportDiagnosticState.CRITICAL:
        return {
          qualifier: BDQueryCriteriaType.AND,
          leftQuery: {
            qualifier: BDQueryCriteriaType.IS_EQUAL,
            field: AssetReportFieldType.FLAT_TIRE,
            values: [true],
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.IS_NULL,
            field: AssetReportFieldType.LOW_TIRE,
          },
        } as BDQuery;
      case ReportDiagnosticState.IMPAIRED:
        return {
          qualifier: BDQueryCriteriaType.AND,
          leftQuery: {
            qualifier: BDQueryCriteriaType.IS_EQUAL,
            field: AssetReportFieldType.LOW_TIRE,
            values: [true],
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.IS_NULL,
            field: AssetReportFieldType.FLAT_TIRE,
          },
        } as BDQuery;
      default:
        return {
          qualifier: BDQueryCriteriaType.AND,
          leftQuery: {
            qualifier: BDQueryCriteriaType.AND,
            leftQuery: {
              qualifier: BDQueryCriteriaType.IS_NULL,
              field: AssetReportFieldType.LOW_TIRE,
            },
            rightQuery: {
              qualifier: BDQueryCriteriaType.IS_NULL,
              field: AssetReportFieldType.FLAT_TIRE,
            },
          },
          //TODO:Remove the condition when API is ready to handle the tire status for null case
          rightQuery: {
            qualifier: BDQueryCriteriaType.AND,
            leftQuery: {
              qualifier: BDQueryCriteriaType.GREATER_THAN,
              field: AssetReportFieldType.LEFT_FRONT_TIRE,
              values: [0],
            },
            rightQuery: {
              qualifier: BDQueryCriteriaType.GREATER_THAN,
              field: AssetReportFieldType.FRONT_EXP_TIRE,
              values: [0],
            },
          },
        } as BDQuery;
    }
  });
  return buildQueryTree(queries, 0, queries.length - 1, false);
};

const createFaultStatusQuery = (
  fieldName: string,
  fault: ReportDiagnosticState[]
) => {
  const isCritical = fault.find(
    (status) => status === ReportDiagnosticState.CRITICAL
  );
  const isImpaired = fault.find(
    (status) => status === ReportDiagnosticState.IMPAIRED
  );
  if (isImpaired && isCritical) {
    return {
      qualifier: BDQueryCriteriaType.OR,
      leftQuery: {
        qualifier: BDQueryCriteriaType.IS_EQUAL,
        field: fieldName,
        values: [ReportDiagnosticState.CRITICAL],
      },
      rightQuery: {
        qualifier: BDQueryCriteriaType.IS_EQUAL,
        field: fieldName,
        values: [ReportDiagnosticState.IMPAIRED],
      },
    };
  }
  return createInQuery(fieldName, fault);
};
const createOilLifeQuery = (oilLifeStatus: OilLifeStatus[]) => {
  const queries = oilLifeStatus.map((status) => {
    switch (status) {
      case OilLifeStatus.CRITICAL:
        return {
          qualifier: BDQueryCriteriaType.IS_EQUAL,
          field: AssetReportFieldType.LOW_OIL_LIFE_STATUS,
          values: [true],
        } as BDQuery;
      case OilLifeStatus.IMPAIRED:
        return {
          qualifier: BDQueryCriteriaType.IS_EQUAL,
          field: AssetReportFieldType.MEDIUM_OIL_LIFE_STATUS,
          values: [true],
        } as BDQuery;
      case OilLifeStatus.GOOD:
        return {
          qualifier: BDQueryCriteriaType.AND,
          leftQuery: {
            qualifier: BDQueryCriteriaType.AND,
            leftQuery: {
              qualifier: BDQueryCriteriaType.IS_NULL,
              field: AssetReportFieldType.MEDIUM_OIL_LIFE_STATUS,
            },
            rightQuery: {
              qualifier: BDQueryCriteriaType.IS_NULL,
              field: AssetReportFieldType.LOW_OIL_LIFE_STATUS,
            },
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.GREATER_THAN,
            field: AssetReportFieldType.OIL_LIFE_VALUE,
            values: [0],
          },
        } as BDQuery;
      default:
        return {
          qualifier: BDQueryCriteriaType.AND,
          leftQuery: {
            qualifier: BDQueryCriteriaType.AND,
            leftQuery: {
              qualifier: BDQueryCriteriaType.IS_EQUAL,
              field: AssetReportFieldType.LOW_OIL_LIFE_STATUS,
              values: [true],
            },
            rightQuery: {
              qualifier: BDQueryCriteriaType.IS_EQUAL,
              field: AssetReportFieldType.MEDIUM_OIL_LIFE_STATUS,
              values: [true],
            },
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.GREATER_THAN,
            field: AssetReportFieldType.OIL_LIFE_VALUE,
            values: [0],
          },
        } as BDQuery;
    }
  });
  return buildQueryTree(queries, 0, queries.length - 1, false);
};

const createBrakePadQuery = (
  brakePadFront: boolean,
  brakePadStatus: BrakePadStatus[]
) => {
  const queries = brakePadStatus.map((status) => {
    switch (status) {
      case BrakePadStatus.CRITICAL:
        return {
          qualifier: BDQueryCriteriaType.IS_EQUAL,
          field: brakePadFront
            ? AssetReportFieldType.LOW_BRAKE_PAD_FRONT_STATUS
            : AssetReportFieldType.LOW_BRAKE_PAD_REAR_STATUS,
          values: [true],
        } as BDQuery;
      case BrakePadStatus.IMPAIRED:
        return {
          qualifier: BDQueryCriteriaType.IS_EQUAL,
          field: brakePadFront
            ? AssetReportFieldType.MEDIUM_BRAKE_PAD_FRONT_STATUS
            : AssetReportFieldType.MEDIUM_BRAKE_PAD_REAR_STATUS,
          values: [true],
        } as BDQuery;
      case BrakePadStatus.GOOD:
        return {
          qualifier: BDQueryCriteriaType.AND,
          leftQuery: {
            qualifier: BDQueryCriteriaType.AND,
            leftQuery: {
              qualifier: BDQueryCriteriaType.IS_NULL,
              field: brakePadFront
                ? AssetReportFieldType.MEDIUM_BRAKE_PAD_FRONT_STATUS
                : AssetReportFieldType.MEDIUM_BRAKE_PAD_REAR_STATUS,
            },
            rightQuery: {
              qualifier: BDQueryCriteriaType.IS_NULL,
              field: brakePadFront
                ? AssetReportFieldType.LOW_BRAKE_PAD_FRONT_STATUS
                : AssetReportFieldType.LOW_BRAKE_PAD_REAR_STATUS,
            },
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.GREATER_THAN,
            field: brakePadFront
              ? AssetReportFieldType.BRAKE_PAD_FRONT_VALUE
              : AssetReportFieldType.BRAKE_PAD_FRONT_VALUE,
            values: [0],
          },
        } as BDQuery;
      default:
        return {
          qualifier: BDQueryCriteriaType.AND,
          leftQuery: {
            qualifier: BDQueryCriteriaType.AND,
            leftQuery: {
              qualifier: BDQueryCriteriaType.IS_EQUAL,
              field: brakePadFront
                ? AssetReportFieldType.LOW_BRAKE_PAD_FRONT_STATUS
                : AssetReportFieldType.LOW_BRAKE_PAD_REAR_STATUS,
              values: [true],
            },
            rightQuery: {
              qualifier: BDQueryCriteriaType.IS_EQUAL,
              field: brakePadFront
                ? AssetReportFieldType.MEDIUM_BRAKE_PAD_FRONT_STATUS
                : AssetReportFieldType.MEDIUM_BRAKE_PAD_REAR_STATUS,
              values: [true],
            },
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.GREATER_THAN,
            field: brakePadFront
              ? AssetReportFieldType.BRAKE_PAD_FRONT_VALUE
              : AssetReportFieldType.BRAKE_PAD_FRONT_VALUE,
            values: [0],
          },
        } as BDQuery;
    }
  });
  return buildQueryTree(queries, 0, queries.length - 1, false);
};

const matchesAnyHealthFlag = (fields: string[]): BDQuery =>
  buildQueryTree(
    fields.map((field) => ({
      qualifier: BDQueryCriteriaType.IS_EQUAL,
      field,
      values: [true],
    })),
    0,
    fields.length - 1,
    false
  ) as BDQuery;

const isFalseQuery = (field: string): BDQuery =>
  ({
    qualifier: BDQueryCriteriaType.IS_EQUAL,
    field,
    values: [false],
  }) as BDQuery;

const isNullQuery = (field: string): BDQuery =>
  ({
    qualifier: BDQueryCriteriaType.IS_NULL,
    field,
  }) as BDQuery;

const allOf = (queries: BDQuery[]): BDQuery =>
  buildQueryTree(queries, 0, queries.length - 1, true) as BDQuery;

/**
 * Mirrors the backend's okVehicles rule: either every summary reported healthy, or the
 * diagnostics summaries are absent and only prognostics reported healthy.
 */
const createOkHealthQuery = (includePrognostics: boolean): BDQuery => {
  const diagnosticsHealthy = allOf([
    isFalseQuery(AssetReportFieldType.WARNING_DIAGNOSTICS),
    isFalseQuery(AssetReportFieldType.CRITICAL_DIAGNOSTICS),
  ]);

  if (!includePrognostics) {
    return diagnosticsHealthy;
  }

  return {
    qualifier: BDQueryCriteriaType.OR,
    leftQuery: allOf([
      isFalseQuery(AssetReportFieldType.WARNING_DIAGNOSTICS),
      isFalseQuery(AssetReportFieldType.CRITICAL_DIAGNOSTICS),
      isFalseQuery(AssetReportFieldType.CRITICAL_PROGNOSTICS),
    ]),
    rightQuery: allOf([
      isNullQuery(AssetReportFieldType.WARNING_DIAGNOSTICS),
      isNullQuery(AssetReportFieldType.CRITICAL_DIAGNOSTICS),
      isFalseQuery(AssetReportFieldType.CRITICAL_PROGNOSTICS),
    ]),
  } as BDQuery;
};

const createDiagnosticsHealthQuery = (
  diagnosticsHealth: ReportDiagnosticState[],
  includePrognostics = false
) => {
  const criticalFields: string[] = [AssetReportFieldType.CRITICAL_DIAGNOSTICS];
  // Prognostics are always critical, so there is no impaired counterpart to match.
  const impairedFields: string[] = [AssetReportFieldType.WARNING_DIAGNOSTICS];

  if (includePrognostics) {
    criticalFields.push(AssetReportFieldType.CRITICAL_PROGNOSTICS);
  }

  const queries = diagnosticsHealth.map((status) => {
    if (status === ReportDiagnosticState.CRITICAL) {
      return matchesAnyHealthFlag(criticalFields);
    }
    if (status === ReportDiagnosticState.IMPAIRED) {
      return matchesAnyHealthFlag(impairedFields);
    }
    return createOkHealthQuery(includePrognostics);
  });
  return buildQueryTree(queries, 0, queries.length - 1, false);
};
const BINARY_OPTIONS_COUNT = 2;
const createAvailabilityQuery = (
  availability: AssetServiceStatus[],
  field: string
) => {
  if (!availability?.length || availability.length === BINARY_OPTIONS_COUNT) {
    return undefined;
  }
  return createInQuery(field, availability);
};
const createFuelQuery = (
  fuelStatus: FuelStatus[] | BatterySocStatus[],
  field: string
) => {
  const makeInclusiveQuery = (
    value: number,
    isGreaterThan: boolean
  ): BDQuery => {
    return {
      qualifier: BDQueryCriteriaType.OR,
      leftQuery: {
        qualifier: isGreaterThan
          ? BDQueryCriteriaType.GREATER_THAN
          : BDQueryCriteriaType.LESS_THAN,
        field,
        values: [value],
      },
      rightQuery: {
        qualifier: BDQueryCriteriaType.IS_EQUAL,
        field,
        values: [value],
      },
    };
  };
  const queries = fuelStatus.flatMap((status) => {
    const { start, end, inclusiveStart, inclusiveEnd } =
      FUEL_CHARGE_FILTER_MODAL_STATUS[status];

    const query: BDQuery = {
      qualifier: BDQueryCriteriaType.AND,
      leftQuery: start
        ? inclusiveStart
          ? makeInclusiveQuery(start, true)
          : {
              qualifier: BDQueryCriteriaType.GREATER_THAN,
              field,
              values: [start],
            }
        : makeInclusiveQuery(MIN_VALID_FUEL_VALUE, true),
      rightQuery: end
        ? inclusiveEnd
          ? makeInclusiveQuery(end, false)
          : {
              qualifier: BDQueryCriteriaType.LESS_THAN,
              field,
              values: [end],
            }
        : makeInclusiveQuery(MAX_VALID_SOC_VALUE, false),
    };
    return [query];
  });
  return buildQueryTree(queries, 0, queries.length - 1, false);
};

const createEstimatedRangeQuery = (
  estimatedRange: EstimatedRangeStatus[],
  field: string
) => {
  const makeInclusiveQuery = (
    value: number,
    isGreaterThan: boolean
  ): BDQuery => {
    return {
      qualifier: BDQueryCriteriaType.OR,
      leftQuery: {
        qualifier: isGreaterThan
          ? BDQueryCriteriaType.GREATER_THAN
          : BDQueryCriteriaType.LESS_THAN,
        field,
        values: [value],
      },
      rightQuery: {
        qualifier: BDQueryCriteriaType.IS_EQUAL,
        field,
        values: [value],
      },
    };
  };
  const queries = estimatedRange.flatMap((status) => {
    const { start, end, inclusiveStart, inclusiveEnd } =
      ESTIMATED_RANGE_MODAL_STATUS[status];

    let query: BDQuery;
    if (end) {
      query = {
        qualifier: BDQueryCriteriaType.AND,
        leftQuery: start
          ? inclusiveStart
            ? makeInclusiveQuery(start, true)
            : {
                qualifier: BDQueryCriteriaType.GREATER_THAN,
                field,
                values: [start],
              }
          : makeInclusiveQuery(MIN_VALID_ESTIMATED_RANGE_VALUE, true),
        rightQuery: inclusiveEnd
          ? makeInclusiveQuery(end, false)
          : {
              qualifier: BDQueryCriteriaType.LESS_THAN,
              field,
              values: [end],
            },
      } as BDQuery;
    } else {
      query = start
        ? inclusiveStart
          ? makeInclusiveQuery(start, true)
          : {
              qualifier: BDQueryCriteriaType.GREATER_THAN,
              field,
              values: [start],
            }
        : makeInclusiveQuery(MIN_VALID_ESTIMATED_RANGE_VALUE, true);
    }
    return [query];
  });
  return buildQueryTree(queries, 0, queries.length - 1, false);
};

const createEngineAirFilterQuery = (
  engineAirFilterStatus: ReportDiagnosticState[]
) => {
  const queries = engineAirFilterStatus.map((status) => {
    switch (status) {
      case ReportDiagnosticState.CRITICAL:
        return {
          qualifier: BDQueryCriteriaType.IS_EQUAL,
          field: AssetReportFieldType.LOW_ENGINE_AIR_FILTER_STATUS,
          values: [true],
        } as BDQuery;
      case ReportDiagnosticState.IMPAIRED:
        return {
          qualifier: BDQueryCriteriaType.IS_EQUAL,
          field: AssetReportFieldType.MEDIUM_ENGINE_AIR_FILTER_STATUS,
          values: [true],
        } as BDQuery;
      case ReportDiagnosticState.GOOD:
        return {
          qualifier: BDQueryCriteriaType.AND,
          leftQuery: {
            qualifier: BDQueryCriteriaType.AND,
            leftQuery: {
              qualifier: BDQueryCriteriaType.IS_NULL,
              field: AssetReportFieldType.MEDIUM_ENGINE_AIR_FILTER_STATUS,
            },
            rightQuery: {
              qualifier: BDQueryCriteriaType.IS_NULL,
              field: AssetReportFieldType.LOW_ENGINE_AIR_FILTER_STATUS,
            },
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.GREATER_THAN,
            field: AssetReportFieldType.ENGINE_AIR_FILTER_VALUE,
            values: [0],
          },
        } as BDQuery;
      default:
        return {} as BDQuery;
    }
  });
  return buildQueryTree(queries, 0, queries.length - 1, false);
};
const createDieselExhaustFluidFilterQuery = (
  engineAirFilterStatus: ReportDiagnosticState[]
) => {
  const queries = engineAirFilterStatus.map((status) => {
    switch (status) {
      case ReportDiagnosticState.CRITICAL:
        return {
          qualifier: BDQueryCriteriaType.IS_EQUAL,
          field: AssetReportFieldType.LOW_DIESEL_EXHAUST_FLUID,
          values: [true],
        } as BDQuery;
      case ReportDiagnosticState.GOOD:
        return {
          qualifier: BDQueryCriteriaType.IS_EQUAL,
          field: AssetReportFieldType.GOOD_DIESEL_EXHAUST_FLUID,
          values: [true],
        } as BDQuery;
      default:
        return {} as BDQuery;
    }
  });
  return buildQueryTree(queries, 0, queries.length - 1, false);
};
const createConnectivityBoardingStatusQuery = (
  connectivityBoardingStatus: ConnectivityStatusType[]
) => {
  const queries = connectivityBoardingStatus.map((status) => {
    // Always use new mapping logic
    switch (status) {
      case ConnectivityStatusType.VERIFICATION_PENDING:
        return {
          qualifier: BDQueryCriteriaType.IS_EQUAL,
          field: AssetReportFieldType.CONNECTIVITY_PRIMARY_OWNERSHIP_STATUS,
          values: [OrganizationOwnershipStatus.CLAIMING],
        } as BDQuery;
      case ConnectivityStatusType.VERIFIED:
      case ConnectivityStatusType.NOT_EQUIPPED:
        return {
          qualifier: BDQueryCriteriaType.AND,
          leftQuery: {
            qualifier: BDQueryCriteriaType.IS_EQUAL,
            field: AssetReportFieldType.CONNECTIVITY_PRIMARY_OWNERSHIP_STATUS,
            values: [OrganizationOwnershipStatus.CLAIMED],
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.NOT_IN,
            field: AssetReportFieldType.ONSTAR_SUPPORT_STATUS,
            values: [OnstarSupportStatus.SUPPORTED],
          },
        } as BDQuery;
      case ConnectivityStatusType.CONNECTION_PENDING:
        return {
          qualifier: BDQueryCriteriaType.AND,
          leftQuery: {
            qualifier: BDQueryCriteriaType.IS_EQUAL,
            field: AssetReportFieldType.CONNECTIVITY_PRIMARY_OWNERSHIP_STATUS,
            values: [OrganizationOwnershipStatus.CLAIMED],
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.AND,
            leftQuery: {
              qualifier: BDQueryCriteriaType.IS_EQUAL,
              field: AssetReportFieldType.ONSTAR_SUPPORT_STATUS,
              values: [OnstarSupportStatus.SUPPORTED],
            },
            rightQuery: {
              qualifier: BDQueryCriteriaType.NOT_IN,
              field: AssetReportFieldType.ONSTAR_CONNECTION_STATUS,
              values: [OnstarConnectionStatus.CONNECTED],
            },
          },
        } as BDQuery;
      case ConnectivityStatusType.CONNECTED:
        return {
          qualifier: BDQueryCriteriaType.AND,
          leftQuery: {
            qualifier: BDQueryCriteriaType.IS_EQUAL,
            field: AssetReportFieldType.CONNECTIVITY_PRIMARY_OWNERSHIP_STATUS,
            values: [OrganizationOwnershipStatus.CLAIMED],
          },
          rightQuery: {
            qualifier: BDQueryCriteriaType.AND,
            leftQuery: {
              qualifier: BDQueryCriteriaType.IS_EQUAL,
              field: AssetReportFieldType.ONSTAR_SUPPORT_STATUS,
              values: [OnstarSupportStatus.SUPPORTED],
            },
            rightQuery: {
              qualifier: BDQueryCriteriaType.IS_EQUAL,
              field: AssetReportFieldType.ONSTAR_CONNECTION_STATUS,
              values: [OnstarConnectionStatus.CONNECTED],
            },
          },
        } as BDQuery;
      default:
        return undefined as unknown as BDQuery;
    }
  });
  return buildQueryTree(queries, 0, queries.length - 1, false);
};
export const createFilterQuery = (
  filterType: AssetListFilterCriteria | undefined,
  fieldNames: AssetRequestFieldNames,
  includePrognostics = false
): (BDQuery | undefined)[] => {
  if (!filterType) {
    return [];
  }
  const queries = [];
  if (filterType.assetType?.length) {
    queries.push(createInQuery(fieldNames.type, filterType.assetType));
  }
  if (filterType.faultStatus?.length) {
    queries.push(
      createFaultStatusQuery(fieldNames.milState, filterType.faultStatus)
    );
  }
  if (filterType.lastCommunicatedStatus?.length) {
    queries.push(
      createLastCommunicatedQuery(
        filterType.lastCommunicatedStatus,
        AssetReportFieldType.LAST_COMMUNICATED
      )
    );
  }
  if (filterType.readyStatus?.length) {
    queries.push(createInQuery(fieldNames.ready, filterType.readyStatus));
  }
  if (filterType.tireStatus?.length) {
    queries.push(createTiresQuery(filterType.tireStatus));
  }
  if (filterType.batteryChargingStatus?.length) {
    queries.push(
      createChargeStatusQuery(
        filterType.batteryChargingStatus,
        AssetReportFieldType.CHARGING_STATUS
      )
    );
  }
  if (filterType.fuelStatus?.length) {
    queries.push(createFuelQuery(filterType.fuelStatus, fieldNames.fuel));
  }
  if (filterType.estimatedRangeStatus?.length) {
    queries.push(
      createEstimatedRangeQuery(
        filterType.estimatedRangeStatus,
        fieldNames.estimatedRange
      )
    );
  }

  if (filterType.powerType?.length) {
    queries.push(
      createPowerTypeQuery(
        filterType.powerType,
        AssetReportFieldType.CHARGING_POWER_TYPE
      )
    );
  }
  if (filterType.batterySocStatus?.length) {
    queries.push(
      createFuelQuery(filterType.batterySocStatus, fieldNames.battery)
    );
  }
  if (filterType.brakeFluidStatus?.length) {
    queries.push(
      createDiagnosticQuery(filterType.brakeFluidStatus, fieldNames.brakeFluid)
    );
  }
  if (filterType.washerFluidStatus?.length) {
    queries.push(
      createDiagnosticQuery(
        filterType.washerFluidStatus,
        fieldNames.washerFluid
      )
    );
  }
  if (filterType.diagnosticsHealthStatus?.length) {
    queries.push(
      createDiagnosticsHealthQuery(
        filterType.diagnosticsHealthStatus,
        includePrognostics
      )
    );
  }
  if (filterType.availabilityStatus?.length) {
    queries.push(
      createAvailabilityQuery(
        filterType.availabilityStatus,
        fieldNames.availability
      )
    );
  }
  if (filterType.oilLifeStatus?.length) {
    queries.push(createOilLifeQuery(filterType.oilLifeStatus));
  }
  if (filterType.brakePadFrontStatus?.length) {
    queries.push(createBrakePadQuery(true, filterType.brakePadFrontStatus));
  }
  if (filterType.brakePadRearStatus?.length) {
    queries.push(createBrakePadQuery(false, filterType.brakePadRearStatus));
  }
  if (filterType.engineAirFilterStatus?.length) {
    queries.push(createEngineAirFilterQuery(filterType.engineAirFilterStatus));
  }
  if (filterType.connectivityBoardingStatus?.length) {
    queries.push(
      createConnectivityBoardingStatusQuery(
        filterType.connectivityBoardingStatus
      )
    );
  }
  if (filterType.dieselExhaustFluidStatus?.length) {
    queries.push(
      createDieselExhaustFluidFilterQuery(filterType.dieselExhaustFluidStatus)
    );
  }
  if (filterType.propulsionType?.length) {
    queries.push(
      createInQuery(fieldNames.propulsionType, filterType.propulsionType)
    );
  }
  if (filterType.locationMaskingStatus?.length) {
    queries.push(
      createInQuery(
        fieldNames.locationMasking,
        filterType.locationMaskingStatus
      )
    );
  }
  if (filterType.fleetIds?.length) {
    queries.push(createInQuery(fieldNames.fleetId, filterType.fleetIds));
  }
  return queries;
};

const createAssignmentQuery = (
  withoutAssignment: boolean | undefined,
  fieldNames: AssetRequestFieldNames
): (BDQuery | undefined)[] => {
  if (!withoutAssignment) {
    return [];
  }
  const queries = [];
  queries.push(createIsNullQuery(fieldNames.fleetId));
  return queries;
};

const createOrgAssignmentQuery = (
  withoutOrgAssignment: boolean | undefined,
  fieldNames: AssetRequestFieldNames
): BDQuery | undefined => {
  if (!withoutOrgAssignment) {
    return undefined;
  }
  return createIsNullQuery(fieldNames.organizationId);
};

const buildContextQuery = (
  context: Partial<RouteParams>,
  fieldNames: AssetRequestFieldNames
) => {
  return context.fleetsId
    ? createEqualQuery(fieldNames.fleetId, [context.fleetsId])
    : context.organizationsId
      ? createEqualQuery(fieldNames.organizationId, [context.organizationsId])
      : undefined;
};

export const buildAssetQuery = (
  config: Partial<AssetListSession> | undefined,
  context: Partial<RouteParams>,
  fieldNames: AssetRequestFieldNames,
  isContextQueryRequired = true,
  includeSelectionCriteria = false,
  includePrognostics = false
): BDQuery | undefined => {
  const searchQuery = createSearchQuery(config?.searchCriteria, fieldNames);

  // Determine vehicle ID query based on selection state
  // Include selection criteria when includeSelectionCriteria is true (dialog data fetching)
  // Skip selection criteria when includeSelectionCriteria is false (normal pagination)
  // If allSelected is true and excludedVehicleIds exists, use NOT_IN to exclude those IDs
  // If allSelected is false and includedVehicleIds exists, use IN to include those IDs
  // Otherwise, no vehicle ID query
  let vehicleIdQuery: BDQuery | undefined;

  if (includeSelectionCriteria) {
    const allSelected = config?.allSelected;

    if (
      allSelected &&
      config?.excludedVehicleIds &&
      !isEmpty(config.excludedVehicleIds)
    ) {
      // All selected with exclusions: exclude the excluded IDs
      vehicleIdQuery = createQuery(
        BDQueryCriteriaType.NOT_IN,
        'id',
        keys(config.excludedVehicleIds) as string[]
      );
    } else if (!allSelected && config && !isEmpty(config?.includedVehicleIds)) {
      // Not all selected: include only the selected IDs
      vehicleIdQuery = createQuery(
        BDQueryCriteriaType.IN,
        'id',
        keys(config.includedVehicleIds) as string[]
      );
    }
  }

  const filterQuery = createFilterQuery(
    config?.filterType,
    fieldNames,
    includePrognostics
  );
  const assignmentQuery = createAssignmentQuery(
    config?.withoutAssignment,
    fieldNames
  );
  const assetContextQuery = buildContextQuery(context, fieldNames);
  const filteredQueries = isContextQueryRequired
    ? ([
        searchQuery,
        assetContextQuery,
        vehicleIdQuery,
        ...filterQuery,
        ...assignmentQuery,
        createOrgAssignmentQuery(
          config?.withoutOrganizationAssignment,
          fieldNames
        ),
      ].filter((query) => query !== undefined) as BDQuery[])
    : ([
        searchQuery,
        vehicleIdQuery,
        ...filterQuery,
        ...assignmentQuery,
        createOrgAssignmentQuery(
          config?.withoutOrganizationAssignment,
          fieldNames
        ),
      ].filter((query) => query !== undefined) as BDQuery[]);
  return createAssetQueryTree(filteredQueries);
};
export const buildVehicleQuery = (
  config: Partial<AssetListSession> | undefined,
  fieldNames: AssetRequestFieldNames,
  includePrognostics = false
): BDQuery | undefined => {
  const searchQuery = createSearchQuery(config?.searchCriteria, fieldNames);

  // Determine vehicle ID query based on selection state
  // If allSelected is true and excludedVehicleIds exists, use NOT_IN to exclude those IDs
  // If allSelected is false and includedVehicleIds exists, use IN to include those IDs
  // Otherwise, no vehicle ID query
  let vehicleIdQuery: BDQuery | undefined;
  const allSelected = config?.allSelected;

  if (
    allSelected &&
    config?.excludedVehicleIds &&
    !isEmpty(config.excludedVehicleIds)
  ) {
    // All selected with exclusions: exclude the excluded IDs
    vehicleIdQuery = createQuery(
      BDQueryCriteriaType.NOT_IN,
      'id',
      keys(config.excludedVehicleIds) as string[]
    );
  } else if (!allSelected && config && !isEmpty(config?.includedVehicleIds)) {
    // Not all selected: include only the selected IDs
    vehicleIdQuery = createQuery(
      BDQueryCriteriaType.IN,
      'id',
      keys(config.includedVehicleIds) as string[]
    );
  }

  const filterQuery = createFilterQuery(
    config?.filterType,
    fieldNames,
    includePrognostics
  );
  const assignmentQuery = createAssignmentQuery(
    config?.withoutAssignment,
    fieldNames
  );

  const filteredQueries = vehicleIdQuery
    ? ([
        searchQuery,
        vehicleIdQuery,
        ...filterQuery,
        ...assignmentQuery,
        createOrgAssignmentQuery(
          config?.withoutOrganizationAssignment,
          fieldNames
        ),
      ].filter((query) => query !== undefined) as BDQuery[])
    : ([
        searchQuery,
        ...filterQuery,
        ...assignmentQuery,
        createOrgAssignmentQuery(
          config?.withoutOrganizationAssignment,
          fieldNames
        ),
      ].filter((query) => query !== undefined) as BDQuery[]);

  return createAssetQueryTree(filteredQueries);
};
export const buildAssetSort = (
  config: Partial<AssetListSession> | undefined,
  fieldNames: AssetRequestFieldNames
): BDSort[] | undefined => {
  if (!config?.sortOrder?.name) {
    return undefined;
  }
  const propertyName =
    fieldNames[config?.sortOrder?.name as keyof AssetRequestFieldNames] ||
    config?.sortOrder?.name;
  const uiDirection =
    config.sortOrder.direction ?? DEFAULT_SORT_ORDER.direction;
  const apiDirection = getApiSortDirection(config.sortOrder.name, uiDirection);
  return [
    {
      direction: apiDirection.toUpperCase() as BDSortDirection,
      properties: [propertyName],
    },
  ];
};

const createComplianceFilterQuery = (
  isChargeStatusFlagEnabled: boolean,
  filterCriteria?: AssetComplianceFilterCriteria
) => {
  if (!filterCriteria) {
    return [];
  }
  const queries = [];
  if (filterCriteria.selectedAssetTypes?.length) {
    queries.push(
      createInQuery(
        AssetReportFieldType.ASSET_TYPE,
        filterCriteria.selectedAssetTypes
      )
    );
  }

  if (filterCriteria.selectedSafetyComplianceStatus) {
    queries.push(
      createSafetyCompliantQuery(
        [filterCriteria.selectedSafetyComplianceStatus],
        AssetComplianceFieldType.SAFETY_COMPLIANT,
        AssetComplianceFieldType.CHARGING_COMPLIANT,
        isChargeStatusFlagEnabled
      )
    );
  }
  return queries;
};
export const buildAssetComplianceQuery = (
  config: Partial<AssetComplianceSession> | undefined,
  context: Partial<RouteParams>,
  isChargeStatusFlagEnabled: boolean
): BDQuery | undefined => {
  const searchQuery = createSearchQuery(
    config?.searchCriteria,
    ASSET_REPORT_FIELD_NAMES
  );
  const assetContextQuery = buildContextQuery(
    context,
    ASSET_REPORT_FIELD_NAMES
  );
  const filterQuery = createComplianceFilterQuery(
    isChargeStatusFlagEnabled,
    config?.filterCriteria
  );
  const filteredQueries = [
    searchQuery,
    assetContextQuery,
    ...filterQuery,
  ].filter((query) => query !== undefined) as BDQuery[];
  return createAssetQueryTree(filteredQueries);
};
// To display the target SOC value depending upon charging status
export const getTargetSocValue = (
  targetSoc: number,
  chargingStatus: BatteryChargingStatus,
  format?: DiagnosticValueFormat
): string => {
  const showTargetSoc =
    chargingStatus === BatteryChargingStatus.CHARGING ||
    chargingStatus === BatteryChargingStatus.COMPLETED;
  return showTargetSoc ? percentLabel(targetSoc, format) : DEFAULT_EMPTY_VALUE;
};

export const formatFilterCriteriaForVehicles = (
  filter: AssetListFilterCriteria | undefined
) => {
  if (!isEmpty(filter)) {
    const updateKeys = (obj: AssetListFilterCriteria) => {
      if (obj) {
        return mapKeys(obj, (_value, key) => {
          return (
            ASSET_REPORT_FIELD_NAMES[key as keyof AssetRequestFieldNames] || key
          );
        });
      }
    };
    const mappedFilterCriteria = updateKeys(
      filter as AssetListSession[`filterType`]
    );
    return omitBy(
      mappedFilterCriteria,
      (value) => isArray(value) && isEmpty(value)
    );
  }
  return undefined;
};

export const formatSearchCriteriaForVehicles = (
  search: SearchCriteria | undefined
) => {
  if (!isEmpty(search)) {
    const searchType = get(search as SearchCriteria, 'searchType');
    const input = get(search, 'input');

    if (!input) {
      return undefined;
    }

    let searchCriteria: Record<string, string>;
    if (searchType === SearchType.COMBINED_VEHICLE_SEARCH) {
      // For combined search, include both id and name fields
      searchCriteria = {
        id: input,
        name: input,
      };
    } else {
      searchCriteria = {
        [searchType]: input,
      };
    }

    const mappedSearchCriteria = mapKeys(searchCriteria, (_value, key) => {
      return (
        ASSET_SEARCH_FIELD_NAMES[key as keyof AssetRequestFieldNames] || key
      );
    });
    return mappedSearchCriteria;
  }
  return undefined;
};
const advisorVehicleFieldSearchTypes: Record<string, string> = {
  [SearchType.ASSET]: 'name',
  [SearchType.VIN]: 'vin',
  [SearchType.LICENSE_PLATE]: 'licensePlate',
};
export const buildAdvisorVehicleQueryParam = ({
  search,
  filter,
}: {
  filter?: AssetListFilterCriteria;
  search?: SearchCriteria;
}) => {
  const queryParams = {};
  //Search to query
  const searchKey = advisorVehicleFieldSearchTypes[search?.searchType || ''];
  if (searchKey) {
    set(queryParams, searchKey, search?.input);
  }

  // Filter to query
  if (get(filter, 'advisorFilterType.length')) {
    set(queryParams, 'propulsionTypes', filter?.advisorFilterType);
  }
  if (get(filter, 'propulsionType.length')) {
    set(queryParams, 'propulsionTypes', filter?.propulsionType);
  }
  return queryParams;
};
/**
 *
 * This function processes license plate strings and returns a formatted value or null for invalid/empty inputs.
 * It specifically handles the default license plate value "000000" by returning null, which allows the UI
 * to display a placeholder (like "--") instead of showing the default value.
 *
 * @param licensePlate - The license plate value to format
 * @returns Formatted license plate string, or null if the input is empty, whitespace-only, undefined, or equals "000000"
 *
 */
export const formatLicensePlate = (licensePlate?: string): string | null => {
  if (
    !licensePlate ||
    licensePlate.trim() === '' ||
    licensePlate === DEFAULT_LICENSE_PLATE_VALUE
  ) {
    return null;
  }

  return licensePlate;
};
